import { useEffect, useMemo, useRef, useState } from 'react'
import './style.scss'

interface SubFilterSectionProps {
	value: number[] | undefined
	stepCount: number
	stepCounts: number[]
	minValue: number
	maxValue: number
	onValueUpdate: (newValue: number[] | undefined) => void
}

class SliderData {
	slidingMin: boolean = true
	parentSliderLeft: number = 0
	parentSliderWidth: number = 0
	eventX: number = 0
	minIndex: number = 0
	maxIndex: number = 0
}

const sliderData: SliderData = new SliderData()

function AppRangeSliderRange({value, stepCount, stepCounts, minValue, maxValue, onValueUpdate}: SubFilterSectionProps) {
	const VALUE_INDICATOR_RADIUS: number = 10

	const rangeSliderRef = useRef<HTMLDivElement>(null)
	const [isSliding, setIsSliding] = useState<boolean>(false)
	const [mouseX, setMouseX] = useState<number>(0)

	// const stepperValues

	const [rangeMinIndex, setRangeMinIndex] = useState<number>(0)
	const [rangeMaxIndex, setRangeMaxIndex] = useState<number>(stepCount - 1)

	const [inputPlaceHolder, setInputPlaceHolder] = useState<string | undefined>(undefined)
	const [inputMaxPlaceHolder, setInputMaxPlaceHolder] = useState<string | undefined>(undefined)

	const [largestStepCount, setLargestStepCount] = useState<number>(0)

	useEffect(() => {
		// make sure value indexes is an array at the correct length
		if (value !== undefined && value.length === 2) {
			setRangeMinIndex(value[0])
			setRangeMaxIndex(value[1])
			sliderData.minIndex = value[0]
			sliderData.maxIndex = value[1]
			setInputPlaceHolder(`${getIndexValue(value[0])}`)
			setInputMaxPlaceHolder(`${getIndexValue(value[1])}`)
		} else {
			setRangeMinIndex(0)
			setRangeMaxIndex(stepCount - 1)
			sliderData.minIndex = 0
			sliderData.maxIndex = stepCount - 1
			setInputPlaceHolder(`${minValue}`)
			setInputMaxPlaceHolder(`${maxValue}`)
		}
	}, [value])

	useEffect(() => {
		let highestCount: number = 0
		stepCounts.forEach((count: number) => {
			if (count > highestCount) {
				highestCount = count
			}
		})
		setLargestStepCount(highestCount)
	}, [stepCounts])

	const getIndexValue = (index: number) => {
		const valueRange: number = maxValue - minValue
		return ((valueRange / stepCount) * index) + minValue
	}

	// ================================== Range Slider ===================================

	const onSliderTouchDownMin = (event: React.MouseEvent | React.TouchEvent) => {
		sliderData.slidingMin = true
		onSliderTouchDown(event)
	}
	const onSliderTouchDownMax = (event: React.MouseEvent | React.TouchEvent) => {
		sliderData.slidingMin = false
		onSliderTouchDown(event)
	}

	const onSliderTouchDown = (event: React.MouseEvent | React.TouchEvent) => {
		const isTouchEvent = 'touches' in event
		const boundingRect: DOMRect | undefined = event.currentTarget.parentElement?.getBoundingClientRect()
		if (boundingRect) {
			sliderData.parentSliderLeft = boundingRect.left
		}
		sliderData.parentSliderWidth = event.currentTarget.parentElement?.clientWidth ? event.currentTarget.parentElement?.clientWidth : 0

		if (isTouchEvent) {
			setMouseX(event.touches[0].clientX)
			window.addEventListener('touchend', onSliderTouchUp)
			window.addEventListener('touchmove', onSliderTouchMove)
		} else {
			setMouseX(event.clientX)
			window.addEventListener('mouseup', onSliderMouseUp)
			window.addEventListener('mousemove', onSliderMouseMove)
		}
		setIsSliding(true)
	}

	const onSliderMouseMove = (event: MouseEvent) => {
		updateSliderValue(event.clientX)
	}
	const onSliderTouchMove = (event: TouchEvent) => {
		updateSliderValue(event.touches[0].clientX)
	}

	const onSliderMouseUp = (event: MouseEvent) => {
		updateSliderValue(event.clientX)
		finishSliderValue()
	}
	const onSliderTouchUp = (event: TouchEvent) => {
		if (event.changedTouches.length > 0) updateSliderValue(event.changedTouches[0].clientX)
		finishSliderValue()
	}

	const updateSliderValue = (x: number) => {
		setMouseX(x)
		const { parentSliderLeft, parentSliderWidth } = sliderData
		if (!parentSliderLeft || !parentSliderWidth) return

		const segmentWidth: number = (parentSliderWidth - (VALUE_INDICATOR_RADIUS * 2)) / (stepCount - 1)
		let closestIndex: number = 0
		
		if (x < parentSliderLeft + VALUE_INDICATOR_RADIUS) {
			closestIndex = 0
		} else if (x > parentSliderLeft + parentSliderWidth - VALUE_INDICATOR_RADIUS) {
			closestIndex = stepCount - 1
		} else {
			closestIndex = Math.floor(((x + (segmentWidth / 2)) - (parentSliderLeft + VALUE_INDICATOR_RADIUS)) / segmentWidth)
		}

		if (sliderData.slidingMin) {
			let minIndex: number = 0
			if (closestIndex > rangeMaxIndex - 1) {
				minIndex = rangeMaxIndex - 1
			} else if (closestIndex < 1) {
				minIndex = 0
			} else {
				minIndex = closestIndex
			}
			setRangeMinIndex(minIndex)
			sliderData.minIndex = minIndex
		} else {
			let maxIndex: number = 0
			if (closestIndex < rangeMinIndex + 1) {
				maxIndex = rangeMinIndex + 1
			} else if (closestIndex > stepCount - 1) {
				maxIndex = stepCount - 1
			} else {
				maxIndex = closestIndex
			}
			setRangeMaxIndex(maxIndex)
			sliderData.maxIndex = maxIndex
		}
	}

	const finishSliderValue = () => {
		setIsSliding(false)
		window.removeEventListener('mouseup', onSliderMouseUp)
		window.removeEventListener('mousemove', onSliderMouseMove)
		window.removeEventListener('touchend', onSliderTouchUp)
		window.removeEventListener('touchmove', onSliderTouchMove)
		if (sliderData.minIndex === 0 && sliderData.maxIndex === stepCount - 1) {
			onValueUpdate(undefined)
		} else {
			onValueUpdate([sliderData.minIndex, sliderData.maxIndex])
		}
	}

	const sliderMinStyle = useMemo(
		() => ({
			marginLeft: isSliding && sliderData.slidingMin ? mouseX - sliderData.parentSliderLeft - VALUE_INDICATOR_RADIUS : `calc(((100% - ${VALUE_INDICATOR_RADIUS * 2}px)/${stepCount-1})*${rangeMinIndex})`,
			background: isSliding && sliderData.slidingMin ? '#002da6' : '#2A50BB'
		}),
		[rangeMinIndex, isSliding, mouseX]
	)

	const sliderMaxStyle = useMemo(
		() => ({
			marginRight: isSliding && !sliderData.slidingMin ? sliderData.parentSliderLeft + sliderData.parentSliderWidth - mouseX - VALUE_INDICATOR_RADIUS : `calc(((100% - ${VALUE_INDICATOR_RADIUS * 2}px)/${stepCount-1})*${(stepCount - 1) - (rangeMaxIndex ? rangeMaxIndex : stepCount-1)})`,
			background: isSliding && !sliderData.slidingMin ? '#002da6' : '#2A50BB'
		}),
		[rangeMaxIndex, isSliding, mouseX]
	)

	return (
		<div className='app-range-slider-range-wrap'>
			<div 
				className='app-range-slider-range-range-wrap'
				ref={rangeSliderRef}
			>
				<div className='app-range-slider-range-range-bar-wrap'>
					{stepCounts.map((count: number, index: number) => 
						<div 
							className='app-range-slider-range-range-bar'
							style={{
								content: '',
								width: `calc(${100 / stepCounts.length}% - 2px)`,
								height: `${(100 / largestStepCount) * count}%`,
								marginTop: 'auto',
								// marginBottom: 3,
								background: index >= rangeMinIndex && index <= rangeMaxIndex ? '#2A50BB' : '#ccc'
							}}
							key={`stepBar${index}`}
						/>
					)}
				</div>
				<div 
					className={`app-range-slider-range-range-indicator`}
					style={sliderMinStyle}
					onMouseDown={onSliderTouchDownMin}
					onTouchStart={onSliderTouchDownMin}
				/>
				<div 
					className={`app-range-slider-range-range-indicator-seperator`}
				/>
				<div 
					className={`app-range-slider-range-range-indicator`}
					style={sliderMaxStyle}
					onMouseDown={onSliderTouchDownMax}
					onTouchStart={onSliderTouchDownMax}
				/>
			</div>
			<div className='app-range-slider-range-min-max-wrap'>
				<div className='app-range-slider-range-min-max-value-wrap'>
					<div className='app-range-slider-range-min-max-value-label'>Minimum</div>
					<div className='app-range-slider-range-min-max-value-value'>
						{/* <input
							className='app-range-slider-range-min-max-value-input'
							value={inputPlaceHolder}
							// value={inputPlaceHolder ? inputPlaceHolder : valueLabels[rangeMinIndex ? rangeMinIndex : 0]}
							onChange={(evt) => {
								setInputPlaceHolder(evt.target.value)
								if (valueLabels.includes(evt.target.value)) {
									setRangeMinIndex(valueLabels.indexOf(evt.target.value))
								}
							}}
							onBlur={() => {
								setInputPlaceHolder(undefined)
							}}
						/> */}
						<div className='app-range-slider-range-min-max-value-input'>${inputPlaceHolder}</div>
					</div>
				</div>
				<div className='app-range-slider-range-min-max-value-wrap'>
					<div className='app-range-slider-range-min-max-value-label'>Maximum</div>
					<div className='app-range-slider-range-min-max-value-value'>
						{/* <input
							className='app-range-slider-range-min-max-value-input'
							value={inputMaxPlaceHolder}
							// value={inputMaxPlaceHolder ? inputMaxPlaceHolder : valueLabels[rangeMaxIndex ? rangeMaxIndex : stepCount - 1]}
							onChange={(evt) => {
								setInputMaxPlaceHolder(evt.target.value)
								if (valueLabels.includes(evt.target.value)) {
									setRangeMaxIndex(valueLabels.indexOf(evt.target.value))
								}
							}}
							onBlur={() => {
								setInputMaxPlaceHolder(undefined)
							}}
						/> */}
						<div className='app-range-slider-range-min-max-value-input'>${inputMaxPlaceHolder}</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default AppRangeSliderRange
